import React, { useEffect, useRef, useState } from 'react';
import { useKeyboardShortcuts } from 'hooks/useKeyboardShortcuts/useKeyboardShortcuts';
import { QUERY_STRING } from 'providers/useSearch/SearchContext';
import { isMQDesktop } from 'style/styled-components/cssMediaQueries';
import { KeyboardActions } from 'types/Common';
import { useNavigation } from 'hooks/useNavigation';
import icons from 'style';
import translate from 'utils/fnTranslate';
import { useConfig } from 'providers/useConfig/ConfigContext';
import { useApp } from 'providers/useApp/AppContext';
import useYouboraSearchTracking from 'hooks/useYouboraSearchTracking';
import { savePageInfo } from 'utils/pageInfoStorage';
import {
    animationDuration,
    IconClear,
    IconSearch,
    SearchInput,
    SearchInputContainer,
    SearchInputLabel,
    SearchWrapper,
} from './HeaderSearch.css';

const CLOSE_TIMEOUT = parseInt(animationDuration, 10);

export const HeaderSearch: React.FC<{
    placeholder: string;
    isSearchPage: boolean;
    isOpen: boolean;
    value: string;
    onSearch: (query: string) => void;
    onSubmit: (query: string) => void;
    onClose: () => void;
    onOpen: () => void;
}> = ({ placeholder, value, isSearchPage, onSearch, onSubmit, onClose, onOpen }) => {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [closing, setClosing] = useState<boolean>(false);
    const [cache, setCache] = useState<string[]>([]);
    const [hasValue, setHasValue] = useState<boolean>(false);

    const searchInput = useRef<HTMLInputElement>(null);
    const onSearchTimeoutRef = useRef<number>(null);
    const closeTimeoutRef = useRef<number>(null);

    const { trigger_timeout } = useConfig().config?.app_config?.search_settings ?? {};
    const { isDesktop } = useApp();
    const { location } = useNavigation();

    const { trackSearchQuery } = useYouboraSearchTracking();

    const searchTitleAria = translate('META_TITLE_SEARCH').split(' - ')[0];

    useKeyboardShortcuts({
        escape: onClose,
    });

    const clearSearch = () => {
        if (onSearchTimeoutRef.current) {
            clearTimeout(onSearchTimeoutRef.current);
        }

        setHasValue(false);
        if (searchInput.current) {
            searchInput.current.value = '';
        }
        onSearch('');

        if (isSearchPage) {
            searchInput.current?.focus();
        } else {
            setClosing(true);
        }
    };

    const closeSearch = () => {
        if (onSearchTimeoutRef.current) {
            clearTimeout(onSearchTimeoutRef.current);
        }

        setHasValue(false);
        if (searchInput.current) {
            searchInput.current.value = '';
        }
        setClosing(true);
    };

    const onChange = () => {
        setHasValue(!!searchInput?.current?.value);

        if (onSearchTimeoutRef.current) {
            clearTimeout(onSearchTimeoutRef.current);
        }

        onSearchTimeoutRef.current = window.setTimeout(() => {
            if (onSearch) {
                const query = searchInput?.current?.value?.trim();

                onSearch(query);

                setCache([query, ...cache.slice(0, 5)]);

                if (query?.length > 2) {
                    trackSearchQuery(query);
                }
            }
        }, trigger_timeout);
    };

    const openSearch = () => {
        onOpen();
        savePageInfo('Search');
        setIsOpen(true);
        setClosing(false);

        window.setTimeout(() => {
            searchInput.current?.focus();
        }, 250);
    };

    const onKeyUp = (e) => {
        const query = searchInput?.current?.value?.trim();

        if (e?.key === KeyboardActions.ENTER && query?.length > 2) {
            onSubmit(query);
            trackSearchQuery(query);
        } else if (query?.length === 0) {
            clearSearch();
        }
    };

    const handleBlur = () => {
        if (isMQDesktop() && !isSearchPage) {
            setIsOpen(false);
        }
    };

    useEffect(() => {
        if (closing) {
            if (closeTimeoutRef.current) {
                clearTimeout(closeTimeoutRef.current);
            }
            closeTimeoutRef.current = window.setTimeout(
                () => {
                    onClose();
                    window.setTimeout(() => setClosing(false), 100);
                },
                isDesktop() ? CLOSE_TIMEOUT : 0
            );
        }
    }, [closing]);

    useEffect(() => {
        const urlQueryParams = new URLSearchParams(location.search);
        const term = urlQueryParams.get(QUERY_STRING);

        setIsOpen(isSearchPage);

        if (!isSearchPage) {
            if (searchInput.current) {
                searchInput.current.value = '';
            }
            setHasValue(false);
        } else if (term?.length) {
            searchInput.current.value = term;
            setHasValue(true);
        }
    }, [location.pathname]);

    return (
        <SearchWrapper isOpen={isOpen} closing={closing}>
            <SearchInputContainer>
                <IconSearch
                    data-test-id={'header-search-iconsearch'}
                    svg={icons.searchIcon}
                    onClick={openSearch}
                    $isOpen={isOpen}
                    tabIndex={0}
                    aria-label={searchTitleAria}
                />
                <SearchInputLabel htmlFor="siteSearch">{searchTitleAria}</SearchInputLabel>
                <SearchInput
                    id="siteSearch"
                    role="search"
                    autoComplete="off"
                    data-test-id={'header-search-inputsearch'}
                    defaultValue={value}
                    type="text"
                    placeholder={placeholder}
                    onChange={onChange}
                    onKeyUp={onKeyUp}
                    inputRef={searchInput}
                    onBlur={handleBlur}
                    tabindex={isOpen ? 0 : -1}
                />
                {(isSearchPage || hasValue) && (
                    <IconClear svg={icons.closeIconNew} onClick={closeSearch} aria-label={translate('CLOSE_BUTTON')} tabIndex={0} />
                )}
            </SearchInputContainer>
        </SearchWrapper>
    );
};
